import { Data } from '.generated/templates/Feature.Data.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { useEffect, useState } from 'react';
import { classnames } from 'tailwindcss-classnames';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import { TextWithOptionalLineBreaks } from 'components/helpers/Heading';
import Collapse from 'components/helpers/SvgIcon/icons/collape';
import Expand from 'components/helpers/SvgIcon/icons/expand';
import ArrowCollapse from 'components/helpers/SvgIcon/icons/arrow-collape';
import ArroweExpand from 'components/helpers/SvgIcon/icons/arrow-expand';
import { useIsNRC } from 'lib/util/site-configuration';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { gtmDatalayerPush, ContentDataLayerObject } from 'lib/util/gtm-datalayer';

export type AccordionItemProps = Sitecore.Override.ItemEx &
  Data.Accordion.Fields.AccordionItem & {
    isOpen?: {
      Open?: boolean;
      Refresh: boolean;
    };
  };

const AccordionItem = ({ fields, isOpen }: AccordionItemProps): React.JSX.Element => {
  const isNrc = useIsNRC();
  const [isActive, setIsActive] = useState(false);
  // needed to stop triggering "collapse" gtm on element creation
  const [isDefaultState, setIsDefaultState] = useState(true);
  const isPageEditing = useSitecoreContext().sitecoreContext.pageEditing;
  useEffect(() => {
    if (isOpen?.Open) {
      setIsActive(true);
    } else {
      setIsActive(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isActive && isDefaultState) {
      setIsDefaultState(false);
    }
    if (!isDefaultState) {
      gtmDatalayerPush({
        event: isActive ? 'e_expand' : 'e_collapse',
        contentName: fields?.heading.value,
      } as ContentDataLayerObject);
    }
  }, [isActive, fields.heading.value, isDefaultState]);

  const panelClasses = isActive ? classnames('p-0', 'rte') : classnames('p-0', 'rte', 'hidden');
  return (
    <li className={classnames('accordion-item', 'mb-2')}>
      <div className={classnames('px-0', 'py-1.5', 'bg-theme-secondary-bg', 'mb-2')}>
        {isPageEditing ? (
          <h2 className={classnames('text-body', 'pl-2.5', 'flex')}>
            {isNrc ? (
              <span className={classnames('pr-2.5')}>
                {isActive ? (
                  <ArroweExpand className={classnames('text-blue-morning')} />
                ) : (
                  <ArrowCollapse className={classnames('text-blue-morning')} />
                )}
              </span>
            ) : (
              <span className={classnames('pr-2.5')}>{isActive ? <Collapse /> : <Expand />}</span>
            )}
            <TextWithOptionalLineBreaks
              className={classnames('mb-2', 'text-body', 'pl-2.5', 'heading-sm')}
              tag=""
              field={fields?.heading}
              editable
            />
          </h2>
        ) : (
          <h2 className={classnames('text-body', 'pl-2.5')}>
            <button
              type="button"
              className={classnames('font-semibold', 'flex', 'text-left')}
              onClick={() => setIsActive(!isActive)}
            >
              {isNrc ? (
                <span className={classnames('pr-2.5')}>
                  {isActive ? (
                    <ArroweExpand className={classnames('text-blue-morning')} />
                  ) : (
                    <ArrowCollapse className={classnames('text-blue-morning')} />
                  )}
                </span>
              ) : (
                <span className={classnames('pr-2.5')}>{isActive ? <Collapse /> : <Expand />}</span>
              )}
              <TextWithOptionalLineBreaks
                className={classnames('mb-2', 'text-body', 'pl-2.5', 'heading-sm')}
                tag=""
                field={fields?.heading}
                editable
              />
            </button>
          </h2>
        )}
      </div>
      <div className={classnames('pl-2.5')}>
        <div className={panelClasses}>
          <RichTextA11yWrapper field={fields.description} />
        </div>
      </div>
    </li>
  );
};

export default AccordionItem;
